import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["input", "text", "previews", "removeIcon", "error", "removedInputs"]
    static values = {maxFiles: Number, multiple: Boolean}

    connect() {
        this.previousFiles = []
        this.refreshUI()
    }

    openFileDialog() {
        this.previousFiles = Array.from(this.inputTarget.files || [])
        this.inputTarget.click()
    }

    onChange() {
        const newlySelected = Array.from(this.inputTarget.files || [])
        const existingCount = this.existingCount()
        const remainingSlots = Math.max(0, this.maxFilesValue - existingCount)

        if (!this.multipleValue) {
            const hasDbImage = existingCount >= 1
            const hadPickedNewBefore = (this.previousFiles || []).length >= 1
            const pickedOneNow = newlySelected.length >= 1

            if (hasDbImage && pickedOneNow) {
                this.showError()
                this.inputTarget.value = ""
                this.replaceFiles([])
                this.refreshUI()
                return
            }

            if (hadPickedNewBefore && pickedOneNow) {
                this.showError()
                this.inputTarget.value = ""
                this.replaceFiles(this.previousFiles.slice(0, 1))
                this.refreshUI()
                return
            }

            this.hideError()
            this.replaceFiles(newlySelected.slice(0, 1))
            this.refreshUI()
            this.previousFiles = Array.from(this.inputTarget.files || [])
            return
        }

        const merged = this.mergeFiles(this.previousFiles, newlySelected)

        if (merged.length > remainingSlots) this.showError()
        else this.hideError()

        const clamped = merged.slice(0, remainingSlots)

        this.replaceFiles(clamped)
        this.refreshUI()
        this.previousFiles = Array.from(this.inputTarget.files || [])
    }

    remove(event) {
        const index = Number(event.currentTarget.dataset.index)
        const files = Array.from(this.inputTarget.files || [])

        files.splice(index, 1)

        this.replaceFiles(files)
        this.refreshUI()
        this.previousFiles = files

        if (this.totalCount() <= this.maxFilesValue) this.hideError()
    }

    removeExisting(event) {
        event.preventDefault()

        const preview = event.currentTarget.closest("[data-attachment-id]")
        if (!preview) return

        const attachmentId = preview.dataset.attachmentId
        const inputName = this.element.dataset.removeParamName || "listing[remove_image_attachment_ids][]"

        const hidden = document.createElement("input")
        hidden.type = "hidden"
        hidden.name = inputName
        hidden.value = attachmentId
        this.removedInputsTarget.appendChild(hidden)

        preview.remove()
        this.refreshUI()
        if (this.totalCount() <= this.maxFilesValue) this.hideError()
    }

    replaceFiles(files) {
        const dt = new DataTransfer()
        files.forEach(f => dt.items.add(f))
        this.inputTarget.files = dt.files
    }

    mergeFiles(oldFiles, newFiles) {
        const all = [...oldFiles, ...newFiles]
        const seen = new Set()
        const unique = []

        for (const f of all) {
            const key = `${f.name}|${f.size}|${f.lastModified}`
            if (seen.has(key)) continue
            seen.add(key)
            unique.push(f)
        }
        return unique
    }

    totalCount() {
        const newFiles = Array.from(this.inputTarget.files || []).length
        const existing = this.previewsTarget.querySelectorAll("[data-existing='true']").length
        return newFiles + existing
    }

    refreshUI() {
        const newFiles = Array.from(this.inputTarget.files || [])
        const existingCount = this.previewsTarget.querySelectorAll("[data-existing='true']").length
        const total = newFiles.length + existingCount

        if (total === 0) this.textTarget.textContent = "No images selected"
        else if (total === 1) this.textTarget.textContent = "1 image selected"
        else this.textTarget.textContent = `${total} images selected`

        this.previewsTarget.querySelectorAll("[data-new='true']").forEach(el => el.remove())

        const closeIcon = (this.hasRemoveIconTarget ? this.removeIconTarget.innerHTML : "×")

        newFiles.forEach((file, index) => {
            const url = URL.createObjectURL(file)

            const el = document.createElement("div")
            el.className = "ui-organism-image-upload__preview"
            el.dataset.new = "true"
            el.innerHTML = `
        <img class="ui-organism-image-upload__preview-img" src="${url}" alt="">
        <button type="button"
                class="ui-organism-image-upload__remove"
                aria-label="Remove image"
                data-index="${index}"
                data-action="click->image-upload#remove">
          ${closeIcon}
        </button>
      `

            this.previewsTarget.appendChild(el)

            el.querySelector("img")
                .addEventListener("load", () => URL.revokeObjectURL(url), {once: true})
        })

        if (total > this.maxFilesValue) this.showError()
    }

    existingCount() {
        return this.previewsTarget.querySelectorAll("[data-existing='true']").length
    }

    showError() {
        if (!this.hasErrorTarget) return
        this.errorTarget.hidden = false
    }

    hideError() {
        if (!this.hasErrorTarget) return
        this.errorTarget.hidden = true
    }
}